import type { JobDetail } from '@/../../shared/types/job';
import { formatClientName } from '@jobos/shared';
import { JobActionsCard } from './JobActionsCard';
import { JobNotesCard } from './JobNotesCard';
import { JobDeleteCard } from './JobDeleteCard';
import { JobInfoCard } from './JobInfoCard';
import { JobProjectInfoCard } from './JobProjectInfoCard';
import { JobDatesCard } from './JobDatesCard';
import { JobBillingCard } from './JobBillingCard';
import { JobDetailPrintView } from './JobDetailPrintView';

interface JobDetailOverviewProps {
  job: JobDetail;
  onUpdateField: (field: string, value: any) => Promise<void>;
  onDelete: () => void;
}

export function JobDetailOverview({ job, onUpdateField, onDelete }: JobDetailOverviewProps) {
  return (
    <>
    <div className="hidden print:block">
      <JobDetailPrintView job={job} />
    </div>
    <div className="print:hidden flex flex-col lg:flex-row gap-6">
      <div className="flex-1 min-w-0 space-y-6 pb-6">
        <div className="flex flex-col md:flex-row gap-6">
          <JobInfoCard
            jobId={job.id}
            jobCode={job.jobCode}
            clientId={job.clientId}
            clientName={formatClientName(job.client.name, job.client.firstName, job.client.lastName)}
            manager={job.manager}
            locationId={job.locationId}
            locationAddressLine1={job.location.addressLine1}
            locationAddressLine2={job.location.addressLine2}
            locationCity={job.location.city}
            serviceAddressNote={job.serviceAddressNote}
            onUpdateField={onUpdateField}
          />
          <JobProjectInfoCard
            projectType={job.projectType}
            customProjectType={job.customProjectType}
            disposition={job.disposition}
            description={job.description}
            onUpdateField={onUpdateField}
          />
        </div>

        <JobDatesCard
          noticeDate={job.noticeDate}
          estimateSendDate={job.estimateSendDate}
          startDate={job.startDate}
          completedDate={job.completedDate}
          onUpdateField={onUpdateField}
        />

        <JobBillingCard
          clientId={job.clientId}
          jobId={job.id}
          jobCardStatus={job.jobCardStatus}
          invoiceStatus={job.invoiceStatus}
          lastInvoicedDate={job.lastInvoicedDate}
          invoicePaidDate={job.invoicePaidDate}
          invoiceType={job.invoiceType}
          invoiceNumber={job.invoiceNumber}
          billingAddressLabel={job.billingAddressLabel}
          billingAddressLine1={job.billingAddressLine1}
          billingAddressLine2={job.billingAddressLine2}
          billingCity={job.billingCity}
          billingState={job.billingState}
          billingZip={job.billingZip}
          onUpdateField={onUpdateField}
        />
      </div>

      <div className="w-full lg:w-80 flex flex-col gap-6 pb-6 lg:sticky lg:top-6 lg:self-start">
        <div className="print:hidden">
          <JobActionsCard
            jobId={job.id}
            jobCode={job.jobCode}
            onedrivePath={job.onedrivePath}
            startDate={job.startDate}
            invoiceType={job.invoiceType}
            isArchived={job.isArchived}
            onUpdateField={onUpdateField}
          />
        </div>
        <JobNotesCard jobId={job.id} />
        <div className="print:hidden">
          <JobDeleteCard
            jobId={job.id}
            jobCode={job.jobCode}
            onDelete={onDelete}
          />
        </div>
      </div>
    </div>
    </>
  );
}
